/* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any */
import { AuthProvider } from 'react-admin';

import { ACTUAL_LANDING_PAGE } from '@app/constants';
import authFlowInitialisationService from '@app/services/auth/authFlowInitialisationService';
import { DEFAULT_LANGUAGE } from '@app/utils/languages';
import { Session } from '@ory/client';

import { MultiLingualObj } from '@app/components/common/Table/DefaultTable/types';

import authService from '../../services/auth/authService';

/**
 * Built with the help of:
 * https://marmelab.com/react-admin/AuthProviderWriting.html
 */
const authProvider: AuthProvider = {
  /**
   * Called when the user enters their username and password on the
   * login form.
   * 'true' is returned if the credentials are correct.
   * An error is thrown if the credentials are incorrect, or
   * if there is some issue with the auth service.
   * The message within the error dictates what is shown to the
   * end-user.
   */
  login: async ({ username, password }: any): Promise<any> => {
    let loginFlowParameters = authFlowInitialisationService.getLoginFlowParameters();
    if (!loginFlowParameters.flowId) {
      console.log('Page was refreshed, re-initialising login flow...');
      await authFlowInitialisationService.initialiseLoginFlow();
      loginFlowParameters = authFlowInitialisationService.getLoginFlowParameters();
    }

    try {
      const session: Session = await authService.login(loginFlowParameters, username, password);
      authService.saveSession(session);
      await authService.getAbility(); // refresh permissions
      return { redirectTo: ACTUAL_LANDING_PAGE };
    } catch (error: any) {
      if (error.message === authService.ERROR_INVALID_LOGIN) {
        // TODO i18n
        throw new Error('Invalid username/password. Please check your credentials');
      }
      console.error('Error occurred during login:', error);
      // TODO i18n
      throw new Error('An unknown error occurred. Please try again later');
    }
  },

  /**
   * Called when the user hits the logout button
   * Resolving the promise continues to the login page
   * Not sure what rejecting the promise does, but assuming it brings up
   * some sort of error
   */
  logout: async (params: any): Promise<string | false | void> => {
    console.log('Running auth.logOut()...');
    try {
      await authService.logout();
    } catch (error) {
      console.error('Something went wrong with logging out', error);
    }
    authService.deleteSession();
    authService.deleteAbility();
    await authFlowInitialisationService.initialiseLoginFlow();
  },

  /**
   * Runs whenever a user navigates to a new page.
   * If the promise is resolved, nothing happens.
   * If the promise is rejected, the authProvider.logout() method
   * is called, and the user is redirected to the login apge.
   */
  checkAuth: async (params: any): Promise<void> => {
    try {
      const session: Session = await authService.getUserSession();
      if (!session.active) {
        throw new Error(authService.ERROR_UNAUTHORIZED);
      }
    } catch (error: any) {
      // TODO this error logging will be replaced by
      // a call to some frontend logging service. for now it's
      // important for debugging purposes.
      if (error.message === authService.ERROR_UNAUTHORIZED) {
        console.warn('Session no longer valid, logging out...');
      } else if (error.message === authService.ERROR_SESSION_EXPIRED) {
        console.warn('Session expired, logging out...');
      } else {
        console.error('Unknown error while calling auth', error);
      }
      // THIS SHOULD REDIRECT TO LOGIN
      // Message in this error gets displayed to UI
      throw new Error('Please login to continue');
    }
  },

  /**
   * Runs every time the dataProvider associated with this React Admin
   * instance returns an error
   */
  checkError: (error: any): Promise<void> => {
    console.log('Running CHECKERROR');
    if (error.status === 401 || error.status === 403) {
      return Promise.reject();
    }
    // other error code (404, 500, etc): no need to log out
    return Promise.resolve();
  },

  /**
   * Called to display the user info at the top right-hand corner of the screen
   * after the user logs in (name + email)
   */
  getIdentity: async () => {
    const session: Session = await authService.getUserSession();
    const name: MultiLingualObj = JSON.parse(session?.identity.traits.name);
    return {
      id: session?.identity.traits.email,
      fullName: name[DEFAULT_LANGUAGE],
    };
  },

  // authorization
  getPermissions: async (params: any): Promise<any> => {
    return await authService.getAbility();
  },
};

export default authProvider;
